/**
 * Shared Editor action-policy metadata registry.
 *
 * @precondition Action ids are business-oriented dotted identifiers.
 * @postcondition Exported helpers fail closed for unknown or endpoint-shaped ids.
 * @mutates none
 */

/**
 * Canonical metadata registry for business action identifiers.
 *
 * @invariant Keys are business action ids (no endpoint/path syntax).
 */
export const EDITOR_ACTION_POLICY_REGISTRY = Object.freeze({
  "reports.overview.view": Object.freeze({
    id: "reports.overview.view",
    capability: "reports.named.sales",
    legacyPermissionKey: "reports",
    reportAccess: true,
    mutation: false,
  }),
  "carts.view": Object.freeze({
    id: "carts.view",
    capability: "carts.view",
    legacyPermissionKey: "carts",
    mutation: false,
  }),
  "seo.view": Object.freeze({
    id: "seo.view",
    capability: "seo.view",
    legacyPermissionKey: "seo",
    mutation: false,
  }),
  "users.view": Object.freeze({
    id: "users.view",
    capability: "users.view",
    legacyPermissionKey: "users",
    mutation: false,
  }),
  "access.roles.manage": Object.freeze({
    id: "access.roles.manage",
    capability: "roles.manage",
    mutation: false,
  }),
  "module.list.view": Object.freeze({
    id: "module.list.view",
    capability: "modules.view",
    aggregate: "modules",
    legacyPermissionKey: "modules",
    mutation: false,
  }),
  "module.item.view": Object.freeze({
    id: "module.item.view",
    capability: "modules.view",
    scopeType: "modules",
    minScopeLevel: "view",
    mutation: false,
  }),
  "module.item.save": Object.freeze({
    id: "module.item.save",
    capability: "modules.edit",
    scopeType: "modules",
    minScopeLevel: "edit",
    legacyFallbackCapability: "modules.view",
    mutation: true,
  }),
  "module.item.create": Object.freeze({
    id: "module.item.create",
    capability: "catalog.create",
    scopeType: "modules",
    minScopeLevel: "edit",
    legacyFallbackCapability: "modules.view",
    mutation: true,
  }),
  "module.item.publish": Object.freeze({
    id: "module.item.publish",
    capability: "catalog.publish",
    scopeType: "modules",
    minScopeLevel: "edit",
    mutation: true,
  }),
  "module.item.clone": Object.freeze({
    id: "module.item.clone",
    capability: "catalog.create",
    scopeType: "modules",
    minScopeLevel: "edit",
    legacyFallbackCapability: "modules.view",
    mutation: true,
  }),
  "module.item.delete": Object.freeze({
    id: "module.item.delete",
    capability: "catalog.delete",
    scopeType: "modules",
    minScopeLevel: "edit",
    mutation: true,
  }),
  "module.item.create-linked": Object.freeze({
    id: "module.item.create-linked",
    capability: "catalog.create",
    scopeType: "modules",
    minScopeLevel: "edit",
    mutation: true,
  }),
  "module.item.gallery.mutate": Object.freeze({
    id: "module.item.gallery.mutate",
    scopeType: "modules",
    minScopeLevel: "edit",
    scopeOnly: true,
    mutation: true,
  }),
  "module.item.marketplace.photo": Object.freeze({
    id: "module.item.marketplace.photo",
    capability: "catalog.edit_media",
    scopeType: "modules",
    minScopeLevel: "edit",
    mutation: true,
  }),
  "module.item.prices": Object.freeze({
    id: "module.item.prices",
    capability: "catalog.edit_prices",
    scopeType: "modules",
    minScopeLevel: "edit",
    mutation: true,
  }),
  "module.item.export": Object.freeze({
    id: "module.item.export",
    capability: "catalog.export_excel",
    scopeType: "modules",
    minScopeLevel: "view",
    mutation: false,
  }),
  "module.item.1c.compose": Object.freeze({
    id: "module.item.1c.compose",
    capability: "catalog.edit_1c",
    scopeType: "modules",
    minScopeLevel: "edit",
    mutation: true,
  }),
  "module.item.1c.search": Object.freeze({
    id: "module.item.1c.search",
    capability: "catalog.edit_1c",
    scopeType: "modules",
    minScopeLevel: "edit",
    mutation: true,
  }),
  "module.item.1c.add": Object.freeze({
    id: "module.item.1c.add",
    capability: "catalog.edit_1c",
    scopeType: "modules",
    minScopeLevel: "edit",
    mutation: true,
  }),
  "seo.save": Object.freeze({
    id: "seo.save",
    capability: "seo.edit",
    mutation: true,
  }),
  "services.compare_1c.run": Object.freeze({
    id: "services.compare_1c.run",
    capability: "services.compare_1c",
    mutation: true,
  }),
  "services.refresh_lp_date.run": Object.freeze({
    id: "services.refresh_lp_date.run",
    capability: "services.refresh",
    mutation: true,
  }),
  "pages.save": Object.freeze({
    id: "pages.save",
    capability: "pages.edit",
    mutation: true,
  }),
  "reports.request": Object.freeze({
    id: "reports.request",
    capability: "reports.named.sales",
    reportAccess: true,
    mutation: true,
  }),
  "pages.view": Object.freeze({
    id: "pages.view",
    capability: "pages.view",
    legacyPermissionKey: "pages",
    mutation: false,
  }),
  "system.options.view": Object.freeze({
    id: "system.options.view",
    capability: "system.options.view",
    legacyPermissionKey: "options",
    mutation: false,
  }),
  "system.options.edit": Object.freeze({
    id: "system.options.edit",
    capability: "system.options.edit",
    mutation: true,
  }),
  "orders.message.send": Object.freeze({
    id: "orders.message.send",
    capability: "orders.edit.workflow",
    mutation: true,
  }),
  "oracle.request.skip": Object.freeze({
    id: "oracle.request.skip",
    capability: "oracle.skip",
    mutation: true,
  }),
})

const SCOPE_LEVEL_WEIGHT = Object.freeze({
  none: 0,
  view: 1,
  edit: 2,
})

/**
 * Returns true when action id shape looks like endpoint/path syntax.
 *
 * @precondition actionId may be any runtime value.
 * @postcondition Returns true for '/api/...', 'api/...', or any string containing '/'.
 * @mutates none
 */
export function isEndpointShapedActionId(actionId) {
  if (typeof actionId !== "string") return true
  const normalized = actionId.trim()
  if (!normalized) return true
  if (normalized.includes("/")) return true
  return normalized.startsWith("api.") || normalized.startsWith("api:")
}

/**
 * Resolve known action metadata.
 *
 * @precondition actionId may be any runtime value.
 * @postcondition Returns a frozen metadata object for known business action ids, else null.
 * @mutates none
 */
export function getEditorActionMeta(actionId) {
  if (isEndpointShapedActionId(actionId)) return null
  return EDITOR_ACTION_POLICY_REGISTRY[actionId] || null
}

/**
 * Evaluate whether a concrete scope level satisfies a required minimum.
 *
 * @precondition Levels are one of none|view|edit (unknown values fail closed).
 * @postcondition Returns false when either level is unknown.
 * @mutates none
 */
export function scopeLevelSatisfies(scopeLevel, requiredLevel) {
  const actual = SCOPE_LEVEL_WEIGHT[scopeLevel] ?? -1
  const required = SCOPE_LEVEL_WEIGHT[requiredLevel] ?? -1
  return actual >= 0 && required >= 0 && actual >= required
}
